import { dispatchCustomEvent, EVENTS } from "@/utils/events";
import { getLogger } from "@/utils/logger";
import { JwtPayload } from "jwt-decode";
import {
  ACCESS_TOKEN_STORAGE_KEY,
  CODE_VERIFIER_STORAGE_KEY,
  REFRESH_RETRY_TIMEOUT,
  REFRESH_TOKEN_STORAGE_KEY,
} from "../constants";
import { LogoutReasons, TokenResponse } from "../types";
import {
  createKeycloakAuthUrl,
  createKeycloakLogoutUrl,
  createKeycloakTokenUrl,
  generateState,
} from "../utils/oauth";

import { config } from "@/utils/config";
import {
  deleteToken,
  getExpirationDate,
  getPersistedToken,
  getToken,
  isNative,
  isPersistedTokenValid,
  parseToken,
  persistToken,
} from "../utils/tokens";
import { MobileNavigator } from "./MobileNavigator";

type AccountServiceOptions = {
  authority: string;
  clientId: string;
  redirectUri: string;
};

const log = getLogger("app:auth:service");

export class AccountService {
  public accessToken?: JwtPayload;
  public refreshToken?: JwtPayload;

  public rawRefreshToken?: string;
  public rawAccessToken?: string;

  public isLoginProcessRunning?: boolean;
  public refreshTimeout?: NodeJS.Timeout;

  constructor(public readonly options: AccountServiceOptions) {
    this.options = options;
  }

  async initialize(onToken: (token: string) => void) {
    if (this.isLoginProcessRunning) return;
    this.isLoginProcessRunning = true;

    try {
      const accessToken = await getPersistedToken(ACCESS_TOKEN_STORAGE_KEY);
      const refreshToken = await getPersistedToken(REFRESH_TOKEN_STORAGE_KEY);

      if (!accessToken || !refreshToken) {
        return;
      }

      if (isPersistedTokenValid(accessToken.raw)) {
        await this.reuseTokens(accessToken, onToken);
        return;
      }

      if (isPersistedTokenValid(refreshToken.raw)) {
        this.refreshToken = refreshToken.parsed;
        this.rawRefreshToken = refreshToken.raw;

        await this.refreshTokens();

        if (this.rawAccessToken) {
          onToken(this.rawAccessToken);
        }
      }
    } catch (error) {
      log("Error during initialization:", error);
      await this.clearTokens();
      dispatchCustomEvent(EVENTS.ACCOUNT_LOGOUT, LogoutReasons.TokenExpired);
    } finally {
      this.isLoginProcessRunning = false;
    }
  }

  async silentLogin() {
    /**
     * For now, we will just redirect to Keycloak for login.
     * In the future, we will implement silent login.
     */
  }

  setAccessToken(raw: string, parsed?: JwtPayload) {
    this.accessToken = parsed ?? parseToken(raw);
    this.rawAccessToken = raw;
    dispatchCustomEvent(EVENTS.ACCOUNT_TOKEN_UPDATE, raw);
    this.scheduleRefresh();
    persistToken(ACCESS_TOKEN_STORAGE_KEY, raw);
  }

  scheduleRefresh() {
    this.cancelRefresh();

    if (!this.refreshToken || !this.accessToken) {
      log("Cannot schedule refresh: missing tokens");
      return;
    }

    const expirationDate = getExpirationDate(this.accessToken);
    if (!expirationDate) {
      log("Cannot schedule refresh: no expiration date");
      return;
    }

    const msUntilRefresh = expirationDate.getTime() - Date.now();
    if (msUntilRefresh <= 30000) {
      log("Token expires soon, refreshing immediately");
      this.refreshTokens().catch((error) => {
        log("Immediate token refresh failed:", error);
      });
      return;
    }

    log("Scheduling token refresh in %o ms", msUntilRefresh);
    this.refreshTimeout = setTimeout(async () => {
      try {
        await this.refreshTokens();
      } catch (error) {
        log("Scheduled token refresh failed:", error);
        setTimeout(() => this.scheduleRefresh(), REFRESH_RETRY_TIMEOUT);
      }
    }, msUntilRefresh);
  }

  cancelRefresh() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = undefined;
    }
  }

  async refreshTokens() {
    const tokenUrl = createKeycloakTokenUrl(this.options.authority);

    if (!this.rawRefreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await fetch(tokenUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          grant_type: "refresh_token",
          client_id: this.options.clientId,
          refresh_token: this.rawRefreshToken,
        }),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 400) {
          log("Refresh token invalid, clearing tokens");
          await this.clearTokens();
          dispatchCustomEvent(
            EVENTS.ACCOUNT_LOGOUT,
            LogoutReasons.TokenExpired,
          );
          throw new Error("Refresh token expired");
        }

        throw new Error(`Failed to refresh token: ${response.status}`);
      }

      log("Successfully refreshed auth tokens");
      const tokens: TokenResponse = await response.json();

      this.accessToken = parseToken(tokens.access_token);
      this.refreshToken = parseToken(tokens.refresh_token);
      this.rawRefreshToken = tokens.refresh_token;
      this.rawAccessToken = tokens.access_token;

      persistToken(ACCESS_TOKEN_STORAGE_KEY, tokens.access_token);
      persistToken(REFRESH_TOKEN_STORAGE_KEY, tokens.refresh_token);

      dispatchCustomEvent(EVENTS.ACCOUNT_TOKEN_UPDATE, tokens.access_token);
    } catch (error) {
      log("Error refreshing tokens:", error);
      throw error;
    }
  }

  async reuseTokens(
    accessToken: {
      raw: string;
      parsed: JwtPayload;
    },
    onToken: (token: string) => void,
  ) {
    const refreshToken = await getPersistedToken(REFRESH_TOKEN_STORAGE_KEY);
    if (refreshToken) {
      this.refreshToken = refreshToken.parsed;
      this.rawRefreshToken = refreshToken.raw;
    }

    this.setAccessToken(accessToken.raw, accessToken.parsed);

    if (this.rawAccessToken) {
      log("Successfully found and reused valid access token");
      dispatchCustomEvent(EVENTS.ACCOUNT_TOKEN_UPDATE, this.rawAccessToken);
      onToken(this.rawAccessToken);
    }

    return Promise.resolve();
  }

  isAuthenticated(): boolean {
    return !!(
      this.rawAccessToken && isPersistedTokenValid(this.rawAccessToken)
    );
  }

  async getAccessToken(): Promise<string | null> {
    if (this.rawAccessToken && isPersistedTokenValid(this.rawAccessToken)) {
      return this.rawAccessToken;
    }

    if (this.rawRefreshToken && isPersistedTokenValid(this.rawRefreshToken)) {
      try {
        await this.refreshTokens();
        return this.rawAccessToken || null;
      } catch (error) {
        log("Failed to refresh token in getAccessToken:", error);
        return null;
      }
    }

    return null;
  }

  async redirectToKeycloak(onToken: (token: string) => void) {
    log("Clearing existing tokens before new authentication flow");
    await deleteToken(CODE_VERIFIER_STORAGE_KEY);

    const codeVerifier = generateState();

    log("Generated state for OAuth flow:", codeVerifier);

    const authorizationUrl = createKeycloakAuthUrl({
      clientId: this.options.clientId,
      state: codeVerifier,
      redirectUri: this.options.redirectUri,
      authority: this.options.authority,
    });

    log("Persisting state before redirect");
    await persistToken(CODE_VERIFIER_STORAGE_KEY, codeVerifier);

    if (isNative()) {
      const navigator = new MobileNavigator(this.options.redirectUri);

      try {
        const url = await navigator.navigate(authorizationUrl);
        await this.handleKeycloakRedirect(onToken, url);
      } catch (error) {
        log("Failed to open browser", error);
      }
    } else {
      window.location.href = authorizationUrl;
    }
  }

  async exchangeCodeForToken(code: string) {
    const tokenUrl = createKeycloakTokenUrl(this.options.authority);

    const response = await fetch(tokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        client_id: this.options.clientId,
        redirect_uri: this.options.redirectUri,
        code: code,
      }),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to exchange code for token: ${response.status} ${response.statusText}`,
      );
    }

    return await response.json();
  }

  async handleKeycloakRedirect(
    onToken: (token: string) => void,
    resolvedUrl?: string,
  ) {
    const url = new URL(resolvedUrl || window.location.href);
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");

    if (!code || !state) {
      throw new Error("Code or state missing in the redirect URL");
    }

    const persistedState = await getToken(CODE_VERIFIER_STORAGE_KEY);
    if (persistedState !== state) {
      await this.clearTokens();
      dispatchCustomEvent(EVENTS.ACCOUNT_LOGOUT, LogoutReasons.TokenExpired);
      throw new Error("State mismatch");
    }

    const tokens = await this.exchangeCodeForToken(code);
    if (!tokens) {
      throw new Error("Failed to exchange code for token");
    }

    log("Successfully exchanged code for tokens");

    this.accessToken = parseToken(tokens.access_token);
    this.refreshToken = parseToken(tokens.refresh_token);
    this.rawAccessToken = tokens.access_token;
    this.rawRefreshToken = tokens.refresh_token;
    persistToken(ACCESS_TOKEN_STORAGE_KEY, tokens.access_token);
    persistToken(REFRESH_TOKEN_STORAGE_KEY, tokens.refresh_token);

    log("Clearing code verifier after successful authentication");
    await deleteToken(CODE_VERIFIER_STORAGE_KEY);

    dispatchCustomEvent(EVENTS.ACCOUNT_TOKEN_UPDATE, tokens.access_token);
    onToken(tokens.access_token);
  }

  async clearTokens() {
    this.accessToken = undefined;
    this.refreshToken = undefined;
    this.rawRefreshToken = undefined;
    this.rawAccessToken = undefined;
    this.cancelRefresh();

    await deleteToken(ACCESS_TOKEN_STORAGE_KEY);
    await deleteToken(REFRESH_TOKEN_STORAGE_KEY);
    await deleteToken(CODE_VERIFIER_STORAGE_KEY);
  }

  async logout() {
    const logoutUrl = createKeycloakLogoutUrl(this.options.authority);

    const refreshToken = await getPersistedToken(REFRESH_TOKEN_STORAGE_KEY);

    fetch(logoutUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        client_id: this.options.clientId,
        refresh_token: refreshToken?.raw || "",
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to logout from Keycloak");
        }
        dispatchCustomEvent(EVENTS.ACCOUNT_LOGOUT, LogoutReasons.Manual);
        log("Successfully logged out from Keycloak");
      })
      .finally(() => {
        this.clearTokens();
      });
  }
}

export const authService = new AccountService({
  authority: config.OIDC_AUTHORITY,
  clientId: config.OIDC_CLIENT_ID,
  redirectUri: config.OIDC_REDIRECT_URI,
});
